import { produce } from "immer";
import storage from "@storage";
import * as Actions from "pages/Folders/store/folderActions";
import * as foldersActions from "pages/Folders/store/foldersActions";
import * as conversationActions from "pages/Mail/ReadMessage/store/conversationActions";

import deepmerge from "deepmerge";
import combineMerge from "config/combineMerge";

const initialState = {
  byId: {},
  allIds: [],
  status: storage.get("folderStatus") || {}
};

const prepareById = (byId, folders) => {
  if (folders.length < 1) {
    return byId;
  }
  const prepared = folders.reduce((obj, folder) => {
    const subs = folder.sub_folders.map(sub => {
      return {
        ...sub,
        color: sub.color || "",
        parent_id: folder.id,
        status: folder.name?.toLowerCase() === "trash" ? "intrash" : "active"
      };
    });

    folder.orig_sub_folders = subs;
    folder.sub_folders = subs.map(item => item.id);
    obj[folder.id] = folder;

    return prepareById(obj, subs);
  }, byId);

  return prepared;
};

const folderReducer = produce((draft, action) => {
  switch (action.type) {
    case Actions.FETCH_FOLDERS_SUCCESS: {
      const folders = action.payload.folders;
      const folderIds = folders.map(folder => String(folder.id));

      const byId = prepareById({}, folders);
      draft.byId = byId;

      draft.allIds = [];
      draft.allIds = deepmerge(draft.allIds, folderIds, {
        arrayMerge: combineMerge
      });

      break;
    }

    case foldersActions.ADD_FOLDER_SUCCESS: {
      const payload = action.payload;
      const folder = payload.folder;
      folder["id"] = folder.id.toString();
      const parentId = payload.parentFolderId;

      draft.byId[folder.id] = {
        ...folder,
        sub_folders: []
      };

      if (parentId > 1) {
        if (!draft.byId[parentId].sub_folders) {
          draft.byId[parentId].sub_folders = [];
        }
        draft.byId[parentId].sub_folders.push(folder.id.toString());
        draft.byId[parentId].children++;
        draft.byId[folder.id].parent_id = parentId;
      } else {
        draft.allIds.push(folder.id.toString());
      }
      break;
    }

    case foldersActions.CLEAR_FOLDER_SUCCESS:
      const payload = action.payload;
      const folderName = payload.folderName;

      if (folderName?.toLowerCase() === "trash") {
        const values = Object.values(draft.byId);
        const trash = values.find(item => item.name?.toLowerCase() === "trash");
        draft.byId[trash.id].children = 0;

        const folders = values.map(item => {
          if (item.status === "intrash") {
            return { ...item, status: "deleted" };
          }
          return { ...item };
        });

        const byId = folders.reduce((obj, folder) => {
          obj[folder.id] = folder;
          return obj;
        }, {});

        draft.byId = deepmerge(draft.byId, byId, {
          arrayMerge: combineMerge
        });
      }

      break;

    case Actions.UPDATE_NAME: {
      const payload = action.payload;
      const folder = payload.folderId;

      draft.byId[folder].name = payload.newName;
      draft.byId[folder].absfolderpath = payload.absFolderPath;
      break;
    }

    case Actions.UPDATE_RETENTION: {
      const payload = action.payload;
      const folder = payload.folderId;

      draft.byId[folder].retention = payload.retention;
      break;
    }

    case Actions.UPDATE_UNREAD_COUNT: {
      const payload = action.payload;
      const folderId = payload.folderId;

      draft.byId[folderId].unread = payload.unread;
      draft.byId[folderId].count = payload.count;
      draft.byId[folderId].size = payload.size;

      break;
    }

    case Actions.MOVE_ITEM: {
      const payload = action.payload;
      const { folderId, prevParentFolder, parentFolderId } = payload;

      if (prevParentFolder > 1) {
        const prevParent = draft.byId[prevParentFolder];
        const remaining = (prevParent?.sub_folders || []).filter(
          item => String(item) !== String(folderId)
        );

        if (prevParent) {
          prevParent.sub_folders = remaining;
          prevParent.children = remaining.length;
        }
      } else {
        const index = (draft.allIds || []).findIndex(
          item => String(item) === String(folderId)
        );

        if (index > -1) {
          draft.allIds.splice(index, 1);
        }
      }

      if (parentFolderId > 1) {
        if (!draft.byId[parentFolderId].sub_folders) {
          draft.byId[parentFolderId].sub_folders = [];
        }

        const newParent = draft.byId[parentFolderId];

        if (
          !newParent.sub_folders.some(item => String(item) === String(folderId))
        ) {
          newParent.sub_folders.push(folderId.toString());
        }

        newParent.children = newParent.sub_folders.length;
        draft.byId[folderId].parent_id = parentFolderId;
      } else {
        if (!draft.allIds.some(item => String(item) === String(folderId))) {
          draft.allIds.push(folderId.toString());
        }
        draft.byId[folderId].parent_id = null;
      }

      if (draft.byId[folderId].status === "intrash") {
        draft.byId[folderId].status = "active";
      }
      break;
    }

    case Actions.DELETE_ITEM: {
      const payload = action.payload;
      const folder = payload.folder;

      const values = Object.values(draft.byId);
      const trash = values.find(item => item.name?.toLowerCase() === "trash");

      if (payload.folder in draft.byId) {
        const parentId = draft.byId[folder].parent_id;
        const trashFolder = draft.byId[trash.id];

        if (draft.byId[folder].status === "intrash") {
          trashFolder.sub_folders = trashFolder.sub_folders.filter(
            item => String(item) !== String(folder)
          );
          trashFolder.children = trashFolder.sub_folders.length;
          draft.byId[folder].status = "deleted";
        } else {
          if (
            !trashFolder.sub_folders.some(
              item => String(item) === String(folder)
            )
          ) {
            trashFolder.sub_folders.push(folder);
          }
          trashFolder.children = trashFolder.sub_folders.length;
          draft.byId[folder].status = "intrash";
          draft.byId[folder].parent_id = trash.id;
        }

        if (parentId > 1 && parentId !== trash.id && draft.byId[parentId]) {
          const parent = draft.byId[parentId];
          parent.sub_folders = (parent.sub_folders || []).filter(
            item => String(item) !== String(folder)
          );
          parent.children = parent.sub_folders.length;
        }
      }
      break;
    }

    case Actions.CHANGE_COLOR: {
      const { folderId, color } = action.payload;

      draft.byId[folderId].color = color;
      break;
    }

    case Actions.UPDATE_STATUS: {
      const { folderId, status } = action.payload;

      draft.status[folderId] = status;
      break;
    }

    case conversationActions.REMOVE_MESSAGE_FROM_CONVERSATION: {
      const values = Object.values(draft.byId);
      const item = values.find(item => item.name === "drafts");
      if (item) {
        const id = item.id;
        draft.byId[id].count = draft.byId[id].count - 1;
      }
      break;
    }

    default:
      return draft;
  }
}, initialState);

export default folderReducer;
